import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight, Zap } from "lucide-react";
import { C, Reveal } from "./shared";

const MARQUEE_PHOTO_IDS = [
  "1571260899304-425eee4c7efc",
  "1604177091072-b7b677a077f6",
  "1637589316488-6d4c41b335cd",
  "1558023598-0afa967eac90",
  "1765572144519-1dc1e6e07bc9",
  "1686624386665-4cd01b96d0f6",
  "1682962232755-f1d051ddb638",
  "1645111529167-7ed14ce1ae28",
];

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-white">
      <div className="absolute inset-0 -z-10 opacity-50 anim-orb"
        style={{ background: `radial-gradient(60% 50% at 80% 0%, ${C.green}15 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, ${C.navy}10 0%, transparent 60%)` }} />
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-16 md:pt-10 md:pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <h1 className="font-display h-display text-5xl sm:text-6xl lg:text-7xl">
            <span style={{ color: C.ink }}>Skills </span>
            <span style={{ color: C.green }}>to </span>
            <span style={{ color: C.navy }}>Careers.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
            The placement portal where <span className="font-semibold" style={{ color: C.ink }}>verified skills land real jobs</span> — for students, working professionals, universities, institutes &amp; companies.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup?role=student" data-testid="hero-cta-student"
              className="px-6 py-3 rounded-lg font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] active:translate-y-0"
              style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.navy} 100%)` }}>
              I&apos;m a Student — start free <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link to="/signup?role=company" data-testid="hero-cta-company"
              className="px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 border-2 transition-all duration-300 hover:bg-slate-50 hover:-translate-y-1 hover:shadow-md"
              style={{ borderColor: C.navy, color: C.navy }}>
              Hire verified talent <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500" data-testid="hero-trust-signals">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: C.green }}/> Governance-filtered jobs</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: C.green }}/> Verified certificates</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: C.green }}/> Enterprise-grade data security</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: C.green }}/> Free for students</span>
          </div>
        </Reveal>

        <Reveal delay={2} className="lg:col-span-5 relative">
          <div className="rounded-2xl p-6 text-white shadow-xl anim-float transition-transform hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 100%)` }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full grid place-items-center anim-pulse-ring" style={{ background: `${C.green}30` }}>
                <ShieldCheck size={26} style={{ color: C.green }} />
              </div>
              <div>
                <div className="font-display font-black text-2xl leading-none">VERIFIED TODAY.</div>
                <div className="font-display font-black text-2xl leading-none mt-1" style={{ color: C.green }}>HIRED TOMORROW.</div>
              </div>
            </div>

            <div className="mt-6 relative rounded-xl overflow-hidden shadow-lg ring-1 ring-white/15 bg-white/5"
                 style={{ height: 240 }} data-testid="hero-marquee">
              <div className="marquee-track flex gap-3 px-3 py-3 absolute top-0 left-0 h-full items-center">
                {[...MARQUEE_PHOTO_IDS, ...MARQUEE_PHOTO_IDS].map((id, idx) => (
                  <img
                    key={`${id}-${idx}`}
                    src={`https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=600&h=600&q=70`}
                    alt="Indian student on SkillConnect"
                    className="h-[216px] w-[170px] flex-shrink-0 rounded-lg object-cover shadow-md ring-1 ring-white/10"
                    draggable="false"
                  />
                ))}
              </div>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#001A66] to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#001A66] to-transparent" />
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs text-white/70">
              <Zap size={12} className="anim-blink" style={{ color: C.green }} /> Shortlisted in &lt; 72 hours
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
